import type { AssistantBlock, AssistantMessage, AssistantTurnResult } from "@ide/shared/assistant";

/**
 * Laço do agente (ADR 0025): pede um turno ao modelo, roda as ferramentas que ele chamou (cada
 * uma passa pela permissão da pessoa em `runTool`) e devolve os resultados, até o modelo
 * terminar. Sem React e sem o app: testável com um modelo de mentira.
 */

export type ToolUse = Extract<AssistantBlock, { type: "tool_use" }>;

export type LoopStop = "end" | "cancelled" | "max_tokens" | "refusal" | "limit" | "error";

export interface LoopDeps {
  turn(messages: AssistantMessage[]): Promise<AssistantTurnResult>;
  runTool(use: ToolUse): Promise<{ content: string; isError?: boolean }>;
  /** Cada resposta do modelo, antes de rodar as ferramentas dela. */
  onAssistant?(message: AssistantMessage): void;
  /** Ferramentas que podem rodar juntas (só leitura): ficam em paralelo, na ordem das respostas. */
  parallel?(use: ToolUse): boolean;
  signal: AbortSignal;
  maxTurns: number;
}

export interface LoopResult {
  messages: AssistantMessage[];
  stop: LoopStop;
  error?: { message: string; auth?: boolean };
  usage: { inputTokens: number; outputTokens: number; cacheReadTokens: number; cacheWriteTokens: number };
  model?: string;
  turns: number;
}

const CANCELLED = "A pessoa interrompeu antes desta ferramenta rodar.";
/** Quantas vezes uma resposta cortada pelo tamanho continua sozinha. */
const MAX_CONTINUES = 3;

export async function runLoop(start: AssistantMessage[], deps: LoopDeps): Promise<LoopResult> {
  const messages = [...start];
  const usage = { inputTokens: 0, outputTokens: 0, cacheReadTokens: 0, cacheWriteTokens: 0 };
  let model: string | undefined;
  let turns = 0;
  /** Respostas cortadas pelo tamanho máximo que já continuaram sozinhas. */
  let continues = 0;
  const done = (stop: LoopStop, error?: LoopResult["error"]): LoopResult => ({
    messages,
    stop,
    usage,
    turns,
    ...(model ? { model } : {}),
    ...(error ? { error } : {}),
  });
  while (turns < deps.maxTurns) {
    if (deps.signal.aborted) return done("cancelled");
    turns++;
    const res = await deps.turn(messages);
    if (!res.ok) return done("error", { message: res.error, ...(res.auth ? { auth: true } : {}) });
    model = res.model;
    if (res.usage) {
      usage.inputTokens += res.usage.inputTokens;
      usage.outputTokens += res.usage.outputTokens;
      usage.cacheReadTokens += res.usage.cacheReadTokens;
      usage.cacheWriteTokens += res.usage.cacheWriteTokens;
    }
    if (res.stopReason === "cancelled") return done("cancelled");
    let message = res.message;
    let uses = message.content.filter((b): b is ToolUse => b.type === "tool_use");
    if (res.stopReason === "max_tokens" && uses.length) {
      // A última chamada pode ter vindo cortada: nada roda, e a conversa fica sem chamadas abertas.
      message = { role: "assistant", content: message.content.filter((b) => b.type !== "tool_use") };
      uses = [];
    }
    if (message.content.length || message.raw) messages.push(message);
    deps.onAssistant?.(message);
    if (res.stopReason === "refusal") return done("refusal");
    if (res.stopReason === "max_tokens") {
      // Como o Claude Code: a resposta cortada pelo tamanho segue sozinha, sem a pessoa pedir.
      if (continues >= MAX_CONTINUES || !message.content.length) return done("max_tokens");
      continues++;
      messages.push({
        role: "user",
        content: [
          { type: "text", text: "Continue exatamente de onde parou, sem repetir o que já escreveu." },
        ],
      });
      continue;
    }
    if (!uses.length) return done("end");
    // Toda chamada recebe um resultado (a API exige), mesmo se a pessoa interromper no meio.
    const run = async (use: ToolUse): Promise<AssistantBlock> => {
      if (deps.signal.aborted)
        return { type: "tool_result", toolUseId: use.id, content: CANCELLED, isError: true };
      try {
        const r = await deps.runTool(use);
        return {
          type: "tool_result",
          toolUseId: use.id,
          content: r.content,
          ...(r.isError ? { isError: true } : {}),
        };
      } catch (err) {
        return {
          type: "tool_result",
          toolUseId: use.id,
          content: err instanceof Error ? err.message : String(err),
          isError: true,
        };
      }
    };
    const results: AssistantBlock[] = [];
    // Leituras seguidas rodam juntas; o resto (que pede permissão ou muda algo), uma por vez.
    for (let i = 0; i < uses.length;) {
      const group = [uses[i]!];
      if (deps.parallel?.(uses[i]!))
        while (i + group.length < uses.length && deps.parallel(uses[i + group.length]!))
          group.push(uses[i + group.length]!);
      results.push(...(await Promise.all(group.map(run))));
      i += group.length;
    }
    messages.push({ role: "user", content: results });
  }
  return done("limit");
}
